import { captureException } from '@sentry/react'
import { ErrorKeys } from 'consts/errorKeys'
import slice from 'store/features/aiLibVideo/slice'
import { aiLibSelectors } from 'store/features/aiLibVideo/selectors'
import { aiVideoSelectors } from 'store/features/aiVideo/selectors'
import { projectSelectors } from 'store/features/project/selectors'
import { TAppDispatch, TGetState } from 'store/types'
import { IAiLibVideoListParams } from 'store/types/aiLib'
import { handleApiError } from 'store/utils'
import { ApiError, ReduxThunkError } from 'utils/errors'
import { getAiLibVideos_API } from 'api'

const { actions } = slice

const getHistory =
  (params: IAiLibVideoListParams) =>
  async (dispatch: TAppDispatch, getState: TGetState) => {
    const state = getState()
    const isScoped = aiLibSelectors.isHistoryScopedToCurrentProject(state)
    const currentProjectId = projectSelectors.id(state)

    if (isScoped && !currentProjectId) {
      dispatch(actions.setHistoryList([]))
      dispatch(actions.setHistoryTotalCount(0))
      dispatch(actions.setIsLoaded(true))
      dispatch(actions.setIsInitialLoading(false))
      return
    }

    const isLoadMore = params.offset !== undefined && params.offset > 0

    const isAlreadyLoading = isLoadMore
      ? aiLibSelectors.isLoadingMore(state)
      : aiLibSelectors.isInitialLoading(state)

    if (isAlreadyLoading) return

    if (isLoadMore) {
      dispatch(actions.setIsLoadingMore(true))
    } else {
      dispatch(actions.setIsInitialLoading(true))
    }

    const activeFilters = aiLibSelectors.activeFilters(state)

    try {
      const response = await getAiLibVideos_API({
        ...params,
        videoProjectId: isScoped ? Number(currentProjectId) : undefined,
        ...activeFilters
      })

      if (isLoadMore) {
        dispatch(actions.appendHistoryList(response.data))
      } else {
        dispatch(actions.setHistoryList(response.data))
        dispatch(actions.setIsLoaded(true))
      }
      dispatch(actions.setHistoryTotalCount(response.totalCount!))
    } catch (error) {
      if (!(error instanceof ApiError)) {
        captureException(new ReduxThunkError(error))
      }
      dispatch(handleApiError(ErrorKeys.GENERAL_API_ERROR))
    } finally {
      if (isLoadMore) {
        dispatch(actions.setIsLoadingMore(false))
      } else {
        dispatch(actions.setIsInitialLoading(false))
      }
    }
  }

const changeModel =
  (modelCode: string | null) =>
  (dispatch: TAppDispatch, getState: TGetState) => {
    const state = getState()
    const duration = aiLibSelectors.selectedDuration(state)
    const aspectRatio = aiLibSelectors.selectedAspectRatio(state)
    const quality = aiLibSelectors.selectedQuality(state)

    const model = modelCode
      ? (aiVideoSelectors
          .aiVideoModels(state)
          .find(m => m.model === modelCode) ?? null)
      : null

    dispatch(
      actions.setFilter({
        modelCode,
        duration:
          !model ||
          duration === null ||
          model.options.some(o => o.duration === duration)
            ? duration
            : null,
        aspectRatio:
          !model ||
          aspectRatio === null ||
          model.capabilities.aspectRatios?.includes(String(aspectRatio))
            ? aspectRatio
            : null,
        quality:
          !model ||
          quality === null ||
          model.options.some(o => o.qualities.some(q => q.quality === quality))
            ? quality
            : null
      })
    )
  }

export const aiLibThunks = {
  ...actions,
  getHistory,
  changeModel
}
